import type { ImageLoaderProps } from "next/image";

const DEFAULT_QUALITY = 75;
const THUMBNAIL_MAX_WIDTH = 240;
const CARD_MAX_WIDTH = 640;

interface TransformProfile {
  fit: "cover" | "scale-down";
  defaultQuality: number;
  maxQuality: number;
}

function isCloudflareR2Hostname(hostname: string): boolean {
  return (
    hostname === "tutulyjewellery.com" ||
    hostname.endsWith(".tutulyjewellery.com") ||
    hostname.endsWith(".r2.dev") ||
    hostname.endsWith(".r2.cloudflarestorage.com")
  );
}

function extractOriginalPathname(pathname: string): string | null {
  const marker = "/cdn-cgi/image/";
  const markerIndex = pathname.indexOf(marker);
  if (markerIndex === -1) return null;

  const afterMarker = pathname.slice(markerIndex + marker.length);
  const firstSlashAfterOptions = afterMarker.indexOf("/");
  if (firstSlashAfterOptions === -1) return null;

  return afterMarker.slice(firstSlashAfterOptions);
}

function buildCloudflareUrl(
  origin: string,
  sourcePath: string,
  width: number,
  quality: number,
  fit: TransformProfile["fit"],
): string {
  const params = [
    `width=${width}`,
    `quality=${quality}`,
    `fit=${fit}`,
    "format=auto",
    "metadata=none",
  ];

  return `${origin}/cdn-cgi/image/${params.join(",")}${sourcePath}`;
}

function resolveTransformProfile(width: number): TransformProfile {
  if (width <= THUMBNAIL_MAX_WIDTH) {
    return {
      fit: "cover",
      defaultQuality: 50,
      maxQuality: 55,
    };
  }

  if (width <= CARD_MAX_WIDTH) {
    return {
      fit: "cover",
      defaultQuality: 60,
      maxQuality: 65,
    };
  }

  return {
    fit: "scale-down",
    defaultQuality: 72,
    maxQuality: 72,
  };
}

/**
 * Global Next.js image loader for Tutuly Cloudflare-hosted images.
 *
 * - Keeps static/local paths untouched.
 * - Converts existing R2 URLs into width-aware `/cdn-cgi/image/` URLs.
 * - If src already includes `/cdn-cgi/image/`, strips old options and rebuilds with requested width.
 */
export default function cloudflareImageLoader({ src, width, quality }: ImageLoaderProps): string {
  if (/^(\/|data:|blob:)/.test(src)) {
    return src;
  }

  try {
    const url = new URL(src);
    if (!isCloudflareR2Hostname(url.hostname)) {
      return src;
    }

    const originalPath = extractOriginalPathname(url.pathname) ?? url.pathname;
    const sourcePath = `${originalPath}${url.search}`;
    const profile = resolveTransformProfile(width);
    const requestedQuality = quality ?? DEFAULT_QUALITY;
    const resolvedQuality = Math.min(requestedQuality, profile.maxQuality);
    const fallbackQuality = resolvedQuality || profile.defaultQuality;

    return buildCloudflareUrl(url.origin, sourcePath, width, fallbackQuality, profile.fit);
  } catch {
    return src;
  }
}
